import { buildI18n, renameI18n } from "@repo/shared";
import type { ClientPreference, LocaleCode, LocalizedString } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TranslationButton } from "@/components/ui/translation-button";
import {
  useCreateClientPreference,
  useDeleteClientPreference,
  useUpdateClientPreference,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./client-preferences-table.i18n";

const isSameI18n = (a: LocalizedString, b: LocalizedString) => {
  const keys = Object.keys(a) as LocaleCode[];
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
};

type ClientPreferencesTableProps = {
  portfolioCollectionId: string;
  clientPreferences: ClientPreference[];
};

export function ClientPreferencesTable({
  portfolioCollectionId,
  clientPreferences,
}: ClientPreferencesTableProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const createPreference = useCreateClientPreference();
  const updatePreference = useUpdateClientPreference();
  const deletePreference = useDeleteClientPreference();
  const isPending =
    createPreference.isPending || updatePreference.isPending || deletePreference.isPending;

  const getName = (i18n: LocalizedString) => localize(i18n);

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingPreference, setEditingPreference] = useState<ClientPreference | null>(null);
  const [editName, setEditName] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [preferenceToDelete, setPreferenceToDelete] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setNewName("");
  };

  const cancelEdit = () => {
    setEditingPreference(null);
    setEditName("");
  };

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;

    createPreference.mutate(
      {
        collectionId: portfolioCollectionId,
        data: { nameI18n: buildI18n(name, userLanguage, tenantSupportedLanguages) },
      },
      {
        onSuccess: () => {
          cancelAdd();
          toast.success(t.preferenceAdded);
        },
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToAdd, "destructive"),
      },
    );
  };

  const handleUpdate = () => {
    const name = editName.trim();
    if (!editingPreference || !name) return;
    if (name === getName(editingPreference.nameI18n)) {
      cancelEdit();
      return;
    }

    updatePreference.mutate(
      {
        collectionId: portfolioCollectionId,
        prefId: editingPreference.id,
        data: {
          nameI18n: renameI18n(
            editingPreference.nameI18n,
            name,
            userLanguage,
            tenantSupportedLanguages,
          ),
        },
      },
      {
        onSuccess: () => {
          cancelEdit();
          toast.success(t.saved);
        },
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  const handleTranslationSave = (preference: ClientPreference, nameI18n: LocalizedString) => {
    if (isSameI18n(preference.nameI18n, nameI18n)) return;

    updatePreference.mutate(
      {
        collectionId: portfolioCollectionId,
        prefId: preference.id,
        data: { nameI18n },
      },
      {
        onSuccess: () => toast.success(t.saved),
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  const handleDelete = () => {
    if (!preferenceToDelete) return;

    deletePreference.mutate(
      { collectionId: portfolioCollectionId, prefId: preferenceToDelete },
      {
        onSuccess: () => toast.success(t.preferenceDeleted),
        onError: (err) => showAlert(t.error, err.message || t.failedToDelete, "destructive"),
      },
    );
    setPreferenceToDelete(null);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          {!isAdding && (
            <Button onClick={() => setIsAdding(true)} disabled={isPending}>
              {t.addPreference}
            </Button>
          )}
        </div>

        {clientPreferences.length === 0 && !isAdding ? (
          <EmptyState>{t.noPreferences}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clientPreferences.map((preference) => (
                <TableRow key={preference.id}>
                  <TableCell>
                    {editingPreference?.id === preference.id ? (
                      <div className="flex gap-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder={t.preferenceName}
                          autoFocus
                          disabled={isPending}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdate();
                            if (e.key === "Escape") cancelEdit();
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={handleUpdate}
                          loading={updatePreference.isPending}
                          disabled={isPending}
                        >
                          {t.save}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEdit}
                          disabled={isPending}
                        >
                          {t.cancel}
                        </Button>
                      </div>
                    ) : (
                      <span className="font-medium">{getName(preference.nameI18n)}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {editingPreference?.id !== preference.id && (
                      <div className="flex items-center justify-end">
                        <Button
                          size="icon"
                          tooltip={t.edit}
                          variant="ghost"
                          onClick={() => {
                            setEditingPreference(preference);
                            setEditName(getName(preference.nameI18n));
                          }}
                          disabled={isPending}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <TranslationButton
                          value={preference.nameI18n}
                          onSave={(nameI18n) => handleTranslationSave(preference, nameI18n)}
                          supportedLanguages={tenantSupportedLanguages}
                          disabled={isPending}
                        />
                        <Button
                          size="icon"
                          tooltip={t.delete}
                          variant="ghost"
                          aria-label={t.deletePreference}
                          onClick={() => {
                            setPreferenceToDelete(preference.id);
                            setConfirmDeleteOpen(true);
                          }}
                          disabled={isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {isAdding && (
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t.placeholder}
              autoFocus
              disabled={isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
                if (e.key === "Escape") cancelAdd();
              }}
            />
            <Button
              size="sm"
              onClick={handleAdd}
              loading={createPreference.isPending}
              disabled={isPending}
            >
              {t.addPreference}
            </Button>
            <Button size="sm" variant="outline" onClick={cancelAdd} disabled={isPending}>
              {t.cancel}
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deletePreference}
        description={t.deleteConfirmation}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
